import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { FC, useRef, useState } from "react";
import Button from "~/components/Button";
import { Container } from "~/components/Container";
import Icon from "~/components/Icon";
import { IconNames } from "~/components/Icon/Icon.type";
import Colors from "~/constants/colors";
import styles from "./CustomerSupportSolutions.module.scss";

const tabs = [
  {
    name: "Customer Support Team",
    title: "Customer Support<br/>All Rounders",
    description:
      "Use AI chatbots to engage visitors, qualify leads by asking key questions and route them to the appropriate sales teams.",
    features: [
      "Intelligent Conversational AI",
      "Multi-Source Responses",
      "Learning from Conversations",
    ],
    note: "Ideal for teams needing a smart support system with Ticket Management, HelpDesk and AI Agent all in one platform.",
    items: [
      {
        icon: "chatbot",
        title: "AI Auto-Reply",
        color: "#3BA55C",
        description:
          "Provide 24/7 support in multiple languages and respond to your customers in under 10 seconds.",
        image: "/images/auto_reply.png",
        link: "/solutions/ai-chatbots",
      },
      {
        icon: "user-square",
        title: "Helpdesk",
        color: "#2A327A",
        description:
          "Easily manage a large support team with flexible chat assignment methods and track performance with CSAT reports.",
        image: "/images/helpdesk.png",
        link: "/solutions/helpdesk",
      },
      {
        icon: "tag",
        title: "Ticket Management",
        color: "#364DE7",
        description:
          "Keep support organized, efficient and stress-free for your team and customers.",
        image: "/images/ticket_management.png",
        link: "/solutions/ticket-management",
      },
    ],
    others: [
      {
        icon: "smileys",
        color: "#FFB130",
        title: "Emotion & Intent Detection",
        image: "/images/emotion_intent.png",
      },
      {
        icon: "personalcard",
        color: "#3BA55C",
        title: "AI Collect Lead Forms",
        image: "/images/ai_collect_lead.png",
      },
      {
        icon: "messages-2",
        color: "#2A327A",
        title: "Custom Chat Flows",
        image: "/images/custom_chat_flows.png",
      },
      {
        icon: "global",
        color: "#733FE7",
        title: "Track Team Activity",
        image: "/images/track_team_activity.png",
      },
      {
        icon: "tag-user",
        color: "#3BA55C",
        title: "CSAT Report",
        image: "/images/csat_report.png",
      },
      {
        icon: "tag-user",
        color: "#702DFF",
        title: "Auto Collect Feedback",
        image: "/images/auto_collect_feedback.png",
      },
      {
        icon: "personalcard",
        color: "#FF73B6",
        title: "Collect Phone/Email",
        image: "/images/collect_phone_email.png",
      },
      {
        icon: "tag-user",
        color: "#702DFF",
        title: "Auto-tagging by AI",
        image: "/images/auto_tagging_by_ai.png",
      },
    ],
  },
  {
    name: "Agency",
    title: "Agency<br/>Customize Brandings",
    description:
      "Manage multiple brands, customize experiences and centralize support from one scalable platform.",
    features: [
      "Use your own brand name and logo",
      "Manage bots for different brands",
      "Customized Authentication",
    ],
    note: "Perfect for agencies that manage multiple clients.",
    items: [
      {
        icon: "global-edit",
        color: "#3A98BA",
        title: "White Label for Agencies",
        description:
          "Make CX Genie match your brand's look and feel, giving clients a consistent and professional experience every time.",
        image: "/images/white_label.png",
        link: "/solutions/white-label",
      },
      {
        icon: "chatbot",
        color: "#ED4245",
        title: "Custom chatbot for each brand",
        description:
          "Tailor chatbot experiences to each individual brand, allowing personalized interactions for a unified and efficient customer journey.",
        image: "/images/custom_chatbot.png",
      },
      {
        icon: "document-download",
        color: "#FC8B23",
        title: "Downloadable client reports",
        description:
          "Create and export detailed client reports for managing support tasks, tracking performance, and sharing insights.",
        image: "/images/client_reports.png",
      },
    ],
    others: [
      {
        icon: "personalcard",
        color: "#FF73B6",
        title: "Custom roles & permissions",
        image: "/images/custom_roles_permissions.png",
      },
      {
        icon: "security-safe",
        color: "#2A327A",
        title: "GDPR-compliant data",
        image: "/images/gdpr_compliant_data.png",
      },
      {
        icon: "global",
        color: "#733FE7",
        title: "Multi-language bots",
        image: "/images/multi_language_bots.png",
      },
      {
        icon: "user-tick",
        color: "#FFB130",
        title: "Custom authentication (SSO)",
        image: "/images/custom_authentication_sso.png",
      },
    ],
  },
  {
    name: "E-commerce Business",
    title: "E-commerce<br/>Product based",
    description:
      "Increase your conversation rate with personalized shopping experiences. Offer product for customer, Purchase support and more ...",
    features: [
      "Customer Support & FAQs",
      "Smart Product Query",
      "Post purchase support & feedback collection",
    ],
    note: "Suitable for E-commerce brand with solution for managing multiple products page and large data FAQs.",
    items: [
      {
        icon: "d-cube-scan",
        color: "#FF73B6",
        title: "Product Recommendation with AI",
        description:
          "Keep your product listings accurate and in sync across all channels, saving time & delivering a smooth shopping experience for your customers.",
        image: "/images/product_recommendation.png",
        link: "/solutions/product-data-sync",
      },
      {
        icon: "book",
        color: "#052FC6",
        title: "AI Up-sell Automation",
        description:
          "Proactively suggest products by analyzing customer behavior and preferences.",
        image: "/images/upsell.png",
      },
      {
        icon: "category-2",
        color: "#0AADFF",
        title: "AI Cross-sell automation",
        description:
          "Expand your reach and boost revenue with context-aware product suggestions delivered directly within the customer's preferred platform.",
        image: "/images/cross_sell.png",
      },
    ],
    others: [
      {
        icon: "smileys",
        color: "#FFB130",
        title: "Emotion & Intent Detection",
        image: "/images/emotion_intent_2.png",
      },
      {
        icon: "data-base",
        color: "#3BA55C",
        title: "Connect to product catalog",
        image: "/images/connect_to_product_catalog.png",
      },
      {
        icon: "message-private",
        color: "#2A327A",
        title: "Order confirmation message",
        image: "/images/order_confirmation_message.png",
      },
      {
        icon: "global",
        color: "#733FE7",
        title: "Multi-language Support",
        image: "/images/multi_language_support.png",
      },
      {
        icon: "user-tick",
        color: "#0FB158",
        title: "AI Pre-sale Q&A",
        image: "/images/ai_pre_sale_qa.png",
      },
      {
        icon: "tag-user",
        color: "#702DFF",
        title: "Auto-tag by issue Type",
        image: "/images/auto_tag_by_issue_type.png",
      },
      {
        icon: "personalcard",
        color: "#3BA55C",
        title: "Lead generation & contact form",
        image: "/images/lead_generation_contact_form.png",
      },
    ],
  },
];

const CardItem = ({
  icon,
  color,
  title,
  description,
  link,
  image,
}: {
  icon: string;
  color: string;
  title: string;
  description: string;
  link?: string;
  image: string;
}) => {
  return (
    <div className={styles.card_item_container}>
      <div className={styles.card_item}>
        <div className={styles.header}>
          <div className={styles.icon} style={{ backgroundColor: color }}>
            <Icon name={icon as IconNames} color="#fff" size={32} />
          </div>

          <h4 className={styles.card_title}>{title}</h4>
          <p className={styles.card_description}>{description}</p>

          {link && (
            <Link href={link} target="_blank" className={styles.link}>
              <Icon name="arrow-right-1" color="#727272" size={24} />
            </Link>
          )}
        </div>

        <div className={styles.body}>
          <Image src={image} alt={title} fill />
        </div>
      </div>
    </div>
  );
};

const OtherItem = ({
  icon,
  color,
  title,
  image,
}: {
  icon: string;
  color: string;
  title: string;
  image: string;
}) => {
  return (
    <div className={styles.other_item}>
      <div className={styles.header}>
        <Icon name={icon as IconNames} color={color} size={32} />
        <div className={styles.text}>{title}</div>
      </div>
      <div className={styles.body}>
        <Image src={image} alt={title} fill />
      </div>
    </div>
  );
};

const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
  transition: { duration: 0.4, ease: "easeInOut" },
};

const CustomerSupportSolutions: FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);

  return (
    <section className={styles.customer_support_solutions} id="solutions">
      <Container className="flex flex-col gap-8 !px-4">
        <div className="flex flex-col gap-3 justify-center items-center">
          <h2 className={styles.title} data-aos="fade-down">
            The solutions you need for
            <br className="hidden md:block" /> outstanding customer support
          </h2>
          <p className={styles.description} data-aos="fade-up">
            As one of our customers said - CX Genie replaces more than 9
            different softwares
            <br />
            and provides the entire customer service operation.
          </p>
        </div>

        <div className={styles.solution} data-aos="fade-up">
          <div className={styles.tabs}>
            {tabs.map((tab, index) => (
              <div
                key={tab.name}
                className={classNames(styles.tab_item, {
                  [styles.active]: activeTab === index,
                })}
                onClick={() => {
                  setActiveTab(index);
                  tabRefs.current[index]?.scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                    block: "nearest",
                  });
                }}
                ref={(el) => (tabRefs.current[index] = el)}
              >
                {tab.name}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              className="flex flex-col gap-4"
              key={activeTab}
              {...animation}
            >
              <div className="flex flex-col gap-12 p-4 md:flex-row">
                <div className="flex flex-col gap-6">
                  <h4
                    className={styles.tab_title}
                    dangerouslySetInnerHTML={{
                      __html: tabs[activeTab].title,
                    }}
                  />

                  <div className="flex gap-2">
                    <Link href="/pricing">
                      <Button>Pricing</Button>
                    </Link>
                    <Button
                      className="hidden px-3 md:inline-block"
                      type="outline"
                      variant="secondary"
                      onClick={() => {
                        (window as any)?.demo_conversion?.();
                        window.open(
                          "https://calendar.app.google/9YB8GKe3ZgNt1z3z7",
                          "_blank"
                        );
                      }}
                    >
                      Book Demo
                    </Button>
                  </div>
                </div>

                <div className="flex flex-col gap-[10px] md:max-w-[465px] ml-auto">
                  <p className={styles.tab_description}>
                    {tabs[activeTab].description}
                  </p>
                  <div className={styles.features}>
                    {tabs[activeTab].features.map((feature) => (
                      <div className={styles.feature_item} key={feature}>
                        <Icon
                          name="tick-circle"
                          color={Colors.PRIMARY}
                          size={20}
                        />
                        <p className={styles.text}>{feature}</p>
                      </div>
                    ))}
                  </div>
                  <div className={styles.line} />
                  <p className={styles.tab_note}>{tabs[activeTab].note}</p>
                </div>
              </div>

              <div className={styles.cards}>
                {tabs[activeTab].items.map((item) => (
                  <CardItem key={item.title} {...item} />
                ))}
              </div>

              <div className="flex flex-col gap-4 mt-8">
                <div className="flex flex-col gap-4 p-4 md:justify-between md:flex-row">
                  <h3 className={styles.other_title}>
                    Others features to power your business
                  </h3>
                  <p className={styles.other_description}>
                    Other features that can help you streamline your bussiness
                    <br />
                    with chatbot
                  </p>
                </div>

                <div className={styles.others}>
                  {tabs[activeTab].others.map((other) => (
                    <OtherItem key={other.title} {...other} />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </Container>
    </section>
  );
};

export default CustomerSupportSolutions;
